import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { CARD_VIEW_V2_WIDTH_BY_LAYOUT_SIZE } from '@/utils/card-view-v2-layout';
import { createStyles } from '@/contexts/theme-context';
import { FlatList, Text, View } from 'react-native';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { HorizontalListArrows } from '@/components/horizontal-list-arrows';
import { ItemListOption } from '@/contexts/item-context-types';
import { MinimalOrderableItemListItem } from '@/components/minimal-orderable-item-list-item';
import { MinimalOrderableItemListItemV2 } from '@/components/minimal-orderable-item-list-item-v2';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useItemRecommendationsContext } from '@/contexts/item-recommendations-context';
import { useLayoutSizeValue } from '@/contexts/layout-context';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';

interface Props {
  updateQuantity: UpdateQuantityFn;
}

export const OrderSummaryRecommendations: React.FC<Props> = ({
  updateQuantity,
}) => {
  const { itemRecommendations } = useItemRecommendationsContext();
  const { selectItemListOption } = useItemDetailContext();
  const newItemCardEnabled = useSupplierDynamicConfigValue(
    'card_view_v2_enabled',
    false,
  );
  const cardV2Width = useLayoutSizeValue(CARD_VIEW_V2_WIDTH_BY_LAYOUT_SIZE);
  const [styles] = useStyles();

  const recommendationListItems = useMemo(() => {
    return itemRecommendations.map((ir) => ir.metadata!.item);
  }, [itemRecommendations]);

  if (!recommendationListItems.length) {
    return null;
  }

  return (
    <View style={styles.base}>
      <Text style={[TYPOGRAPHY.medium, styles.title]}>
        {i18n.t('you_might_also_like')}
      </Text>
      <View style={styles.arrowContainer}>
        <HorizontalListArrows<ItemListOption> trackingKey="order_summary_recs_carousel">
          {(listProps) => (
            <FlatList<ItemListOption>
              {...listProps}
              alwaysBounceHorizontal={false}
              contentContainerStyle={styles.listContainer}
              data={recommendationListItems}
              decelerationRate="fast"
              horizontal
              renderItem={({ item }) => {
                if (newItemCardEnabled) {
                  return (
                    <MinimalOrderableItemListItemV2
                      item={item}
                      onSelect={() => {
                        selectItemListOption(
                          item,
                          { source: 'order_summary_recommendations' },
                          {
                            updateQuantity,
                          },
                        );
                      }}
                      updateQuantity={updateQuantity}
                      width={cardV2Width}
                    />
                  );
                }

                return (
                  <MinimalOrderableItemListItem
                    item={item}
                    onSelect={() => {
                      selectItemListOption(
                        item,
                        { source: 'order_summary_recommendations' },
                        {
                          updateQuantity,
                        },
                      );
                    }}
                    source="order_summary_recommendations"
                    updateQuantity={updateQuantity}
                  />
                );
              }}
              showsHorizontalScrollIndicator={false}
            />
          )}
        </HorizontalListArrows>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  arrowContainer: {
    backgroundColor: theme.colors.background,
  },
  base: {
    backgroundColor: theme.colors.background,
    paddingVertical: 8,
  },
  listContainer: {
    paddingHorizontal: 16,
  },
  title: {
    ...FONTS.primaryBold,
    marginBottom: 8,
    marginLeft: 16,
  },
}));
